// ─── Zoom 1 Default Scenario Optimizer ────────────────────────────────────────
//
// Pure module — no React, no Supabase. Proposes the STARTING conversion schedule
// for a JBF CE scenario from the Zoom 1 quick-build variables, so the advisor
// stops guessing conversion amounts live on the call. The proposal is ordinary
// form state (per-year schedule + CE target): every number is visible, editable,
// and nothing is saved until the advisor saves.
//
// DESIGN RULE — search over the REAL engine, never a parallel tax model. Every
// probe runs calcJBFTwoBucketAnnualTable via the same buildJBFTwoBucketParams
// adapter the live Calculate uses, so the SS-taxable ramp, senior deductions,
// OBBBA phase-out, state tax, RMD-before-conversion ordering, January top-ups,
// and forward bracket indexing are correct by construction. The proposal can
// never drift from what the annual table then shows.
//
// PACES:
//  - 'self_funding' (default): each year converts only what the engine's own
//    distributions can pay the tax on (externalTaxPaid ≈ 0), still under the
//    bracket ceiling. With an existing Roth seeding the engine, out-of-pocket
//    is ~$0 from day one; without one, Year 1 is the seed year (bracket-filled,
//    cost-to-start reported) and every later year is CE-covered. The schedule
//    RAMPS as January top-ups grow the engine, then runs flat at the bracket
//    top once the CE target is full.
//  - 'fastest': classic fill-to-bracket-top every year (IRMAA cap optional).
//
// If self-funding pace cannot finish converting the IRA by life expectancy but
// fastest can, the proposal automatically falls back to fastest and says so
// (fellBack = true) — never silently strand IRA dollars.
//
// CONSTRAINTS per year (binding one reported as `boundBy`):
//  - 'bracket'       ordinary taxable income ≤ top of the chosen bracket − buffer
//  - 'irmaa'         MAGI ≤ chosen IRMAA tier bound − buffer (optional; the cap
//                    is applied to conversion-year MAGI, which sets the premium
//                    two years later per the engine's 2-year lookback)
//  - 'ce_coverage'   self-funding pace: engine distributions cover the tax
//  - 'ira_exhausted' the year converts everything the IRA has left
//  - 'rmd_filled'    $0 proposed: base income (incl. RMD) already fills the ceiling
//  - 'no_room'       $0 proposed: base income over the ceiling, no RMD involved
//
// All amounts land on a $5K grid (rounded DOWN — defensible, explainable numbers
// per the June 30 optimizer spec). calcEngine.js is NOT modified by this feature.

import {
  calcJBFTwoBucketAnnualTable,
  buildJBFTwoBucketParams,
  calcFederalTax,
  calcIRMAA,
  medicarePersons,
  CE_TIERS,
  BASE_YEAR,
} from './calcEngine';

export const GRID = 5000;          // proposal rounding grid
const OOP_EPS = 500;               // "You Pay ≈ 0" tolerance (engine rounds to $)
const IRA_DUST = 1000;             // an IRA below this counts as fully converted

export const OPTIMIZER_DEFAULTS = {
  pace: 'self_funding',            // 'self_funding' | 'fastest'
  bracketRate: 0.24,               // 0.22 | 0.24 | 0.32 — governing federal bracket
  bracketBuffer: 0,                // $ under the bracket top (overshoot is cheap)
  irmaaTier: null,                 // null = off; 0 = stay surcharge-free; 1..4 = max tier
  irmaaBuffer: 10000,              // $ under the IRMAA bound (the expensive cliff)
};

// ─── Ceiling helpers (probe the engine's own tax functions — no duplicated tables) ───

// Largest ORDINARY taxable income still inside `rate`'s bracket for the given
// filing status + calendar year. Derived by probing calcFederalTax's marginal
// rate, so it follows the engine's 2026 anchors AND its forward indexing exactly.
const _topCache = new Map();
export function bracketTopTaxable(rate, filingStatus, calendarYear) {
  const key = rate + '|' + filingStatus + '|' + calendarYear;
  if (_topCache.has(key)) return _topCache.get(key);
  const marg = (t) =>
    calcFederalTax(t + 1, filingStatus, calendarYear).tax -
    calcFederalTax(t, filingStatus, calendarYear).tax;
  let res = 0;
  if (marg(1) <= rate + 1e-9) {
    let lo = 1;
    let hi = 20_000_000; // far above any indexed 35% top in a human projection
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (marg(mid) <= rate + 1e-9) lo = mid;
      else hi = mid;
    }
    // `lo` is the last dollar whose NEXT dollar still taxes at `rate`, so the
    // bracket's true upper bound (e.g. $201,775 single 2026) is lo + 1.
    res = lo + 1;
  }
  _topCache.set(key, res);
  return res;
}

// IRMAA tier structure for a filing status + calendar year, probed from
// calcIRMAA (per person) so the engine's bracket indexing carries through.
// bounds[k] = largest MAGI whose surcharge stays at level k (k = 0 → none).
const _irmaaCache = new Map();
export function irmaaTierBounds(filingStatus, calendarYear) {
  const key = filingStatus + '|' + calendarYear;
  if (_irmaaCache.has(key)) return _irmaaCache.get(key);
  const sur = (m) => calcIRMAA(m, filingStatus, 1, calendarYear);
  const levels = [0];
  for (let m = 1000; m <= 1_500_000; m += 1000) {
    const v = sur(m);
    if (v > levels[levels.length - 1]) levels.push(v);
  }
  const bounds = [];
  for (let i = 0; i < levels.length - 1; i++) {
    let lo = 0;
    let hi = 1_500_000;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (sur(mid) <= levels[i] + 1e-9) lo = mid;
      else hi = mid;
    }
    bounds.push(lo);
  }
  const out = { levels, bounds };
  _irmaaCache.set(key, out);
  return out;
}

// How many IRMAA tier bounds a MAGI crosses (0 = surcharge-free).
export function irmaaTierHit(magi, filingStatus, calendarYear) {
  const { bounds } = irmaaTierBounds(filingStatus, calendarYear);
  let n = 0;
  for (const b of bounds) if (magi > b) n++;
  return n;
}

const rowMagi = (row) =>
  (row.grossTaxableIncome || 0) + (row.iraGapGross || 0) + (row.annuityGapDraw || 0);

// ─── The proposer ─────────────────────────────────────────────────────────────

// form / scenario: the live App form + the JBF scenario being proposed for.
// ages: { clientAge, spouseAge } — attained-age convention (same as calculatePlan).
// options: overrides for OPTIMIZER_DEFAULTS.
export function proposeSchedule(form, scenario, ages, options = {}) {
  const opts = { ...OPTIMIZER_DEFAULTS, ...options };
  const clientAge = ages ? ages.clientAge : null;
  const spouseAge0 = ages && ages.spouseAge != null ? ages.spouseAge : null;
  if (clientAge == null || !Number.isFinite(clientAge)) {
    return { ok: false, reason: 'Enter the client date of birth first.' };
  }
  const iraTotal = (parseFloat(form && form.balance401k) || 0) + (parseFloat(form && form.balanceIRA) || 0);
  if (iraTotal <= 0) {
    return { ok: false, reason: 'Enter an IRA / 401k balance to convert first.' };
  }

  const existingRoth = parseFloat(form && form.balanceRothIRA) || 0;
  // Per the locked mechanic: an existing Roth becomes the engine's head start
  // (partial seed; conversions top the engine up to target). Zero tax to create it.
  const seedSource = existingRoth > 0 ? 'existing_roth' : 'nonqual';
  const tier = CE_TIERS[scenario && scenario.tier] || null;
  const distRate = tier ? tier.planningRate : 0.11; // legacy no-tier engine: 11%
  const lifeExp = parseFloat(form && form.lifeExpectancy) || 90;

  // One probe = one full engine run with a candidate schedule. The draft scenario
  // pins the fields the proposal owns; everything else (QCD, SN payoff, SS claiming
  // ages, tier) stays exactly as the advisor set it.
  const runEngine = (schedule, ceTarget) => {
    const draft = {
      ...scenario,
      conversionSchedule: schedule,
      ceTargetSize: ceTarget || 0,
      engineSeedSource: seedSource,
      year1TaxMethod: 'cash',
      years2PlusTaxMethod: 'jbf_distribution',
      year1Amount: '',
      year1ConversionAmount: '',
      conversionAmount: '',
    };
    return calcJBFTwoBucketAnnualTable(
      buildJBFTwoBucketParams(form, draft, { clientAge, spouseAge: spouseAge0 })
    );
  };

  // Solve one pace: forward-sequential, per-year binary search on the $5K grid.
  // Later years never affect earlier rows, so freezing solved years is exact.
  const solvePace = (pace, ceTarget) => {
    const sched = [];      // entries {age, amount} (zeros kept for no-room years)
    const detail = [];     // per-year diagnostics for the proposal card
    let firstFundedAge = null;
    let exhaustedAge = null;
    let lastResult = null;

    for (let age = clientAge; age <= lifeExp; age++) {
      const calYear = BASE_YEAR + (age - clientAge);
      const base = runEngine(sched, ceTarget);
      lastResult = base;
      const baseRow = base.rows.find((r) => r.age === age);
      if (!baseRow) break;
      const fs = baseRow.filingStatus || form.filingStatus || 'mfj';
      const ceiling = bracketTopTaxable(opts.bracketRate, fs, calYear) - (opts.bracketBuffer || 0);

      // IRMAA cap: conversion-year MAGI sets the premium two years later (engine's
      // 2-year lookback), so the cap only matters when someone is Medicare-enrolled
      // in that premium year — and the tier bounds are the premium year's.
      const spouseAtYear = spouseAge0 != null ? spouseAge0 + (age - clientAge) : null;
      const irmaaRelevant =
        opts.irmaaTier != null &&
        medicarePersons(age + 2, spouseAtYear != null ? spouseAtYear + 2 : null) > 0;
      let irmaaBound = null;
      if (irmaaRelevant) {
        const { bounds } = irmaaTierBounds(fs, calYear + 2);
        if (opts.irmaaTier < bounds.length) {
          irmaaBound = bounds[opts.irmaaTier] - (opts.irmaaBuffer || 0);
        }
      }

      const availStart = Math.max(0, baseRow.iraBalanceStart || 0);
      if (availStart <= IRA_DUST) {
        if (exhaustedAge == null) exhaustedAge = age;
        break;
      }

      const check = (result) => {
        const row = result.rows.find((r) => r.age === age);
        if (!row) return { ok: false, why: 'engine', row: null };
        if (row.taxableIncome > ceiling) return { ok: false, why: 'bracket', row };
        if (irmaaBound != null && rowMagi(row) > irmaaBound) return { ok: false, why: 'irmaa', row };
        return { ok: true, row };
      };
      const checkOop = (result, oopCapped) => {
        const c = check(result);
        if (!c.ok) return c;
        if (oopCapped && (c.row.externalTaxPaid || 0) > OOP_EPS) {
          return { ok: false, why: 'ce_coverage', row: c.row };
        }
        return c;
      };

      // Base row must itself be under the ceilings for any conversion to fit.
      const baseCheck = check(base);
      if (!baseCheck.ok) {
        detail.push({
          age, calYear, amount: 0,
          boundBy: (baseRow.rmd || 0) > 0 ? 'rmd_filled' : 'no_room',
          seedYear: false,
          taxableIncome: baseRow.taxableIncome, ceiling,
          magi: rowMagi(baseRow), irmaaBound,
          conversionTaxCost: 0, cePays: 0, youPay: 0, rmd: baseRow.rmd || 0,
          irmaaTierHit: irmaaTierHit(rowMagi(baseRow), fs, calYear + 2),
        });
        sched.push({ age, amount: 0 });
        continue;
      }

      // Self-funding pace: every year is coverage-capped once the engine exists.
      // The engine exists from day one with an existing-Roth seed; otherwise the
      // first funded year is the seed year (bracket-filled, cost-to-start allowed).
      const wantOopCap =
        pace === 'self_funding' && (firstFundedAge != null || seedSource === 'existing_roth');

      const gridSolve = (oopCapped) => {
        const kMax = Math.max(1, Math.ceil(availStart / GRID));
        const probe = (k) => runEngine([...sched, { age, amount: k * GRID }], ceTarget);
        const hiResult = probe(kMax);
        if (checkOop(hiResult, oopCapped).ok) {
          return { k: kMax, result: hiResult, atCap: true };
        }
        let lo = 0;
        let hi = kMax;
        let loResult = base;
        while (hi - lo > 1) {
          const mid = Math.floor((lo + hi) / 2);
          const p = probe(mid);
          if (checkOop(p, oopCapped).ok) { lo = mid; loResult = p; }
          else hi = mid;
        }
        return { k: lo, result: loResult, atCap: false };
      };

      let oopCapped = wantOopCap;
      let solved = gridSolve(oopCapped);
      let seedYear = false;
      if (solved.k === 0 && firstFundedAge == null && oopCapped) {
        // Tiny/absent engine capacity: fall back to a seed year so the plan can start.
        oopCapped = false;
        solved = gridSolve(false);
        seedYear = solved.k > 0;
      }
      if (firstFundedAge == null && solved.k > 0 && seedSource === 'nonqual') seedYear = true;

      const amount = solved.k * GRID;
      const accRow = solved.result.rows.find((r) => r.age === age) || baseRow;
      lastResult = solved.result;

      // Name the constraint that stopped one more $5K step.
      let boundBy;
      if (solved.k === 0) {
        boundBy = (baseRow.rmd || 0) > 0 ? 'rmd_filled' : 'no_room';
      } else if (solved.atCap) {
        boundBy = 'ira_exhausted';
      } else {
        const next = runEngine([...sched, { age, amount: (solved.k + 1) * GRID }], ceTarget);
        boundBy = checkOop(next, oopCapped).why || 'bracket';
      }

      detail.push({
        age, calYear, amount, boundBy, seedYear,
        taxableIncome: accRow.taxableIncome, ceiling,
        headroom: Math.max(0, ceiling - accRow.taxableIncome),
        magi: rowMagi(accRow), irmaaBound,
        conversionTaxCost: accRow.conversionTaxCost || 0,
        cePays: accRow.jbfYieldUsedForTax || 0,
        youPay: accRow.externalTaxPaid || 0,
        rmd: accRow.rmd || 0,
        fundingExhausted: !!accRow.fundingExhausted,
        irmaaTierHit: irmaaTierHit(rowMagi(accRow), fs, calYear + 2),
      });
      sched.push({ age, amount });
      if (solved.k > 0 && firstFundedAge == null) firstFundedAge = age;

      if ((accRow.iraBalance || 0) <= IRA_DUST) {
        exhaustedAge = age;
        break;
      }
    }

    // Trim trailing zero rows (leading/interior zeros stay — they carry tags).
    while (sched.length && sched[sched.length - 1].amount === 0) {
      sched.pop();
      detail.pop();
    }

    const finalRun = runEngine(sched, ceTarget);
    const lastRow = finalRun.rows[finalRun.rows.length - 1] || null;
    const totalConverted = finalRun.rows.reduce((s, r) => s + (r.conversionAmount || 0), 0);
    return {
      pace, sched, detail,
      firstFundedAge,
      complete: exhaustedAge != null,
      conversionCompleteAge: exhaustedAge,
      unconvertedAtLE: exhaustedAge != null ? 0 : Math.round(lastRow ? lastRow.iraBalance || 0 : 0),
      totalOutOfPocket: Math.round(finalRun.totalExternalTaxPaid || 0),
      totalCEPays: Math.round(finalRun.totalJBFTaxFunded || 0),
      totalConverted: Math.round(totalConverted),
      peakConvTaxCost: Math.round(finalRun.peakConvTaxCost || 0),
      ceFullyFundedAge: finalRun.ceFullyFundedAge != null ? finalRun.ceFullyFundedAge : null,
      fundingExhaustedAges: finalRun.rows.filter((r) => r.fundingExhausted).map((r) => r.age),
      finalRun,
    };
  };

  // ── Phase 1: fastest solve sizes the engine ────────────────────────────────
  // Bracket-fill amounts don't depend on the CE target (routing never moves
  // ordinary taxable income), so solve fastest first with target 0, then derive:
  //   CE target = steady-state (peak) conversion tax ÷ tier distribution rate.
  const fastProbe = solvePace('fastest', 0);
  const peakTax = fastProbe.peakConvTaxCost;
  const ceTarget = peakTax > 0 ? Math.ceil(peakTax / distRate / GRID) * GRID : 0;

  // Finalize fastest against the real target. Bucket routing doesn't move ordinary
  // taxable income, so the amounts stand; one cheap verify pass nudges any year
  // down a $5K step if a gap-draw side effect crossed a ceiling, then the detail
  // rows are refreshed from the final run (real coverage / You-Pay numbers).
  const fastest = (() => {
    const sched = fastProbe.sched.map((e) => ({ ...e }));
    let run = runEngine(sched, ceTarget);
    for (let pass = 0; pass < 3; pass++) {
      let violated = false;
      for (const e of sched) {
        if (e.amount <= 0) continue;
        const row = run.rows.find((r) => r.age === e.age);
        if (!row) continue;
        const d = fastProbe.detail.find((x) => x.age === e.age);
        const ceiling = d ? d.ceiling : Infinity;
        const overIrmaa = d && d.irmaaBound != null && rowMagi(row) > d.irmaaBound;
        if (row.taxableIncome > ceiling || overIrmaa) {
          e.amount = Math.max(0, e.amount - GRID);
          violated = true;
        }
      }
      if (!violated) break;
      run = runEngine(sched, ceTarget);
    }
    const lastRow = run.rows[run.rows.length - 1] || null;
    const detail = fastProbe.detail.map((d) => {
      const row = run.rows.find((r) => r.age === d.age);
      const e = sched.find((x) => x.age === d.age);
      if (!row) return d;
      return {
        ...d,
        amount: e ? e.amount : d.amount,
        taxableIncome: row.taxableIncome,
        headroom: Math.max(0, (d.ceiling ?? Infinity) - row.taxableIncome),
        magi: rowMagi(row),
        conversionTaxCost: row.conversionTaxCost || 0,
        cePays: row.jbfYieldUsedForTax || 0,
        youPay: row.externalTaxPaid || 0,
        rmd: row.rmd || 0,
        fundingExhausted: !!row.fundingExhausted,
      };
    });
    return {
      pace: 'fastest', sched, detail,
      firstFundedAge: fastProbe.firstFundedAge,
      complete: fastProbe.complete,
      conversionCompleteAge: fastProbe.conversionCompleteAge,
      unconvertedAtLE: fastProbe.complete ? 0 : Math.round(lastRow ? lastRow.iraBalance || 0 : 0),
      totalOutOfPocket: Math.round(run.totalExternalTaxPaid || 0),
      totalCEPays: Math.round(run.totalJBFTaxFunded || 0),
      totalConverted: Math.round(run.rows.reduce((s, r) => s + (r.conversionAmount || 0), 0)),
      peakConvTaxCost: Math.round(run.peakConvTaxCost || 0),
      ceFullyFundedAge: run.ceFullyFundedAge != null ? run.ceFullyFundedAge : null,
      fundingExhaustedAges: run.rows.filter((r) => r.fundingExhausted).map((r) => r.age),
      finalRun: run,
    };
  })();

  const requestedPace = opts.pace === 'fastest' ? 'fastest' : 'self_funding';
  const selfFunding = requestedPace === 'self_funding' ? solvePace('self_funding', ceTarget) : null;

  // ── Pace selection + auto-fallback ─────────────────────────────────────────
  let chosen;
  let fellBack = false;
  let bothIncomplete = false;
  if (requestedPace === 'fastest') {
    chosen = fastest;
  } else if (selfFunding.complete) {
    chosen = selfFunding;
  } else if (fastest.complete) {
    chosen = fastest;          // never silently strand IRA dollars
    fellBack = true;
  } else {
    chosen = selfFunding;      // neither finishes — keep the requested pace, flag it
    bothIncomplete = true;
  }

  const firstFunded = chosen.detail.find((d) => d.amount > 0) || null;
  const alt = chosen.pace === 'fastest' ? (selfFunding || null) : fastest;

  return {
    ok: true,
    requestedPace,
    pace: chosen.pace,
    fellBack,
    bothIncomplete,
    opts: { ...opts },
    distRate,
    tierKey: tier ? tier.key : null,
    ceTarget,
    peakTax,
    engineSeedSource: seedSource,
    engineSeedFromRoth: seedSource === 'existing_roth'
      ? Math.round(ceTarget > 0 ? Math.min(existingRoth, ceTarget) : existingRoth)
      : 0,
    schedule: chosen.sched.map((e) => ({ age: e.age, amount: e.amount })),
    detail: chosen.detail,
    costToStart: firstFunded ? Math.round(firstFunded.youPay) : 0,
    totalOutOfPocket: chosen.totalOutOfPocket,
    totalCEPays: chosen.totalCEPays,
    totalConverted: chosen.totalConverted,
    conversionCompleteAge: chosen.conversionCompleteAge,
    unconvertedAtLE: chosen.unconvertedAtLE,
    ceFullyFundedAge: chosen.ceFullyFundedAge,
    fundingExhaustedAges: chosen.fundingExhaustedAges,
    alt: alt
      ? {
          pace: alt.pace,
          complete: alt.complete,
          conversionCompleteAge: alt.conversionCompleteAge,
          unconvertedAtLE: alt.unconvertedAtLE,
          totalOutOfPocket: alt.totalOutOfPocket,
          totalConverted: alt.totalConverted,
        }
      : null,
  };
}

// UI labels for the binding-constraint tags (kept here so App and tests agree).
export function boundLabel(boundBy, opts = OPTIMIZER_DEFAULTS) {
  const pct = Math.round((opts.bracketRate || 0.24) * 100);
  switch (boundBy) {
    case 'bracket':       return pct + '% top';
    case 'irmaa':         return 'IRMAA cap';
    case 'ce_coverage':   return 'CE-covered';
    case 'ira_exhausted': return 'IRA done';
    case 'rmd_filled':    return 'RMD-filled';
    case 'no_room':       return 'No room';
    default:              return boundBy || '';
  }
}
